import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { ApiGet } from '../../helper/axiosFunctions';
import { commonToast } from '../../helper/Toast';

// Async thunk to fetch school data
export const fetchSchoolsData = createAsyncThunk(
  'school/fetchSchoolsData',
  async ({ schoolIds, accessToken }, { rejectWithValue }) => {
    try {
      const schoolsData = [];
      
      for (const schoolId of schoolIds) {
        try {
          // Use the access token directly for API calls during login
          const response = await ApiGet(`/contents/schools/${schoolId}`, {}, true, accessToken);
          if (response?.data) {
            schoolsData.push({
              id: schoolId,
              ...response.data
            });
          }
        } catch (error) {
          console.error(`Error fetching school ${schoolId}:`, error);
          // Add a fallback school object if API fails
          schoolsData.push({
            id: schoolId,
            schoolName: `School ${schoolId}`,
            description: "School information unavailable"
          });
        }
      }
      
      return schoolsData;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  selectedSchool: null,
  availableSchools: [],
  loading: false,
  error: null,
  showSchoolModal: false,
  schoolIds: []
};

const schoolSlice = createSlice({
  name: 'school',
  initialState,
  reducers: {
    setSelectedSchool: (state, action) => {
      state.selectedSchool = action.payload;
    },
    setAvailableSchools: (state, action) => {
      state.availableSchools = action.payload;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    setShowSchoolModal: (state, action) => {
      state.showSchoolModal = action.payload;
    },
    setSchoolIds: (state, action) => {
      state.schoolIds = action.payload;
    },
    clearSchoolData: (state) => {
      state.selectedSchool = null;
      state.availableSchools = [];
      state.loading = false;
      state.error = null;
      state.showSchoolModal = false;
      state.schoolIds = [];
    },
    // Action to handle school selection from modal
    selectSchool: (state, action) => {
      state.selectedSchool = action.payload;
    },
    // Action to handle no schools scenario
    continueWithoutSchool: (state) => {
      state.selectedSchool = null;
      state.showSchoolModal = false;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSchoolsData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSchoolsData.fulfilled, (state, action) => {
        state.loading = false;
        state.availableSchools = action.payload;
        state.error = null;
        
        // Auto-select if only one school
        if (action.payload.length === 1) {
          state.selectedSchool = action.payload[0];
        }
      })
      .addCase(fetchSchoolsData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        commonToast("Failed to fetch school information", "error");
      });
  }
});

export const {
  setSelectedSchool,
  setAvailableSchools,
  setLoading,
  setError,
  setShowSchoolModal,
  setSchoolIds,
  clearSchoolData,
  selectSchool,
  continueWithoutSchool
} = schoolSlice.actions;

export default schoolSlice.reducer; 